import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import InvoiceForm from "../pages/InvoiceForm";

// Popup modal for creating / editing an invoice (spec §5 Forms & Modals). A blue
// gradient TOP HEADER (icon + title) sits above the shared InvoiceForm rendered
// in `embedded` mode (no page chrome).
//
// Props:
//   open      — controls visibility.
//   onClose   — close without saving.
//   onSuccess — called after a successful save (receives { isEdit }).
//   editId    — null = create; an id = edit.
function AddInvoiceModal({ open, onClose, onSuccess, editId = null }) {
  const isEdit = editId != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            background: "#fff",
            border: `1px solid ${COLORS.hairline}`,
            boxShadow: "0 24px 64px rgba(16, 24, 40, 0.18)",
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        {/* Blue gradient top header */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2.5, md: 3.5 },
            py: 2.5,
            color: "#fff",
            flexShrink: 0,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -30,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              filter: "blur(40px)",
            }}
          />
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            <ReceiptLongOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.85 }}>
              {isEdit ? "Edit" : "New"}
            </Typography>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 22, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.15 }}>
              {isEdit ? "Edit Invoice" : "Add Invoice"}
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              ml: "auto",
              position: "relative",
              zIndex: 1,
              color: "#fff",
              background: "rgba(255,255,255,0.16)",
              "&:hover": { background: "rgba(255,255,255,0.28)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form body (scrolls if taller than the viewport) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: { xs: 2, md: 3 },
            background: COLORS.canvas,
          }}
        >
          <InvoiceForm
            key={editId ?? "new"}
            embedded
            invoiceId={editId}
            onSuccess={onSuccess}
            onCancel={onClose}
          />
        </Box>
      </Box>
    </Dialog>
  );
}

export default AddInvoiceModal;
